import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import updateUserContext from '../../redux/actions/user';
import DeployQueueActions from '../../redux/actions/deploy-queue';

import { uiConfig, crudConfig } from '../../conf';

// UI Components
import { Button } from '../../../components/Button';
import Table from '../../../components/Table';
import AddEdit from './addEdit';
import Delete from './delete';
import Purge from './purge';
import { getDeployQueueTimeoutProperties, translateTimedOutStatus } from './deployQueueTimeout';

import { translateUIConfigForTable, translateUIConfigForAddEdit, StateReconciler } from '../../../common/utility';

const { _READ_ONLY_FEATURES } = process.env; // Global read-only flag

const DeployQueueManager = ({ userContext }) => {
    const dispatch = useDispatch();
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [purging, setPurging] = useState(null);
    const [fatalError, setFatalError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [timeoutProperties, setTimeoutProperties] = useState(null);
    const saveDisabled = true;
    const deleteDisabled = _READ_ONLY_FEATURES === 'true';

    let { sessionToken, ...context } = userContext;
    useEffect(() => {
        dispatch(updateUserContext(context, sessionToken))
            .then(() => {
                return getDeployQueueTimeoutProperties().then((properties) => {
                    setTimeoutProperties(properties);
                    return dispatch(DeployQueueActions(crudConfig).fetchAll());
                }).then(() => setFatalError(null));
            })
            .catch((err) => {
                setFatalError(err);
            });
    }, [sessionToken]);

    let tableData = [];
    const { data, updated, deleted } = useSelector((state) =>
        StateReconciler.object(state.deploy_queue, uiConfig.primaryKey, ['updated', 'deleted'])
    );
    const { columns, actions, initialState } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    if (actions != null) {
        tableData = Object.values(data).map((v) => {
            const displayItem = translateTimedOutStatus(v, timeoutProperties);
            return {
                ...displayItem,
                DeferredSaveIndicator: v?.status === 'SUCCESS' ? `created` : undefined,
                DataTableActions: actions.map((i) => ({
                    ...i,
                    action: () => i.action(v[uiConfig.primaryKey])
                }))
            };
        });
    }

    const unmountDataCheck = (updated, deleted) => {
        if (typeof updated === 'object') {
            if (Object.keys(updated).length > 0 || deleted.length > 0) {
                return 'You have unsaved changed, are you sure you wish to leave';
            }
        }
    };

    useEffect(() => {
        window.onbeforeunload = () => unmountDataCheck(updated, deleted);
    }, [updated, deleted]);

    useEffect(() => {
        if (fatalError !== null) {
            throw fatalError;
        }
    });

    useEffect(() => {
        return () => {
            unmountDataCheck();
        };
    }, []);

    const maybeFetchEditData = () => {
        if (editing !== 'new') {
            if (typeof data[editing] === 'object') {
                return translateTimedOutStatus(data[editing], timeoutProperties);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
            }
        } else {
            return typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {};
        }
    };

    const fetchDeleteData = () => {
        return data[deleting];
    };

    const closeAddEdit = () => {
        setEditing(null);
    };

    const refreshQueue = () => {
        setRefreshing(true);
        dispatch(DeployQueueActions(crudConfig).fetchAll())
            .then(() => {
                setFatalError(null);
            })
            .catch((err) => {
                setFatalError(err);
            })
            .finally(() => {
                setRefreshing(false);
            });
    };

    const onDelete = () => {
        if (deleteDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return Promise.reject();
        }
        return new Promise((resolve, reject) => {
            dispatch(DeployQueueActions(crudConfig).delete(deleting))
                .then((resp) => {
                    resolve(resp);
                })
                .catch((e) => {
                    reject(e);
                });
        });
    };

    const onPurge = (maxDeploymentDate) => {
        // Check for is new
        // alert("Deferred Save not currently implemented, this is local only edits");
        return new Promise((resolve, reject) => {
            dispatch(DeployQueueActions(crudConfig).purge(maxDeploymentDate ? format(maxDeploymentDate, "yyyy-MM-dd'T'HH:mm:ssxxxx") : undefined))
                .then((resp) => {
                    dispatch(DeployQueueActions(crudConfig).fetchAll()).then(() => {
                        resolve();
                    });
                })
                .catch((e) => {
                    reject(e);
                });
        });
    };

    const closeDelete = () => {
        setDeleting(null);
    };

    const isExistingDoc = (doc) => {
        return (
            Object.values(data).find((v) => {
                return (
                    v.environment === doc.environment &&
                    v.deploymentType === doc.deploymentType &&
                    v.businessId === doc.businessId &&
                    v.scopingEntityId === doc.scopingEntityId &&
                    v.code === doc.code &&
                    v.codeSystem === doc.codeSystem &&
                    v.deploymentDate === doc.deploymentDate &&
                    v.deploymentId !== doc.deploymentId
                );
            }) !== undefined
        );
    };

    const applyChanges = (updatedDoc) => {
        // Check for is new
        // alert("Deferred Save not currently implemented, this is local only edits");
        return new Promise((resolve, reject) => {
            if (editing === 'new') {
                if (!isExistingDoc(updatedDoc)) {
                    dispatch(DeployQueueActions(crudConfig).insert(updatedDoc))
                        .then((resp) => {
                            resolve(resp);
                        })
                        .catch((e) => {
                            reject(e);
                        });
                } else {
                    reject('Matching document already exists.');
                }
            } else {
                if (!isExistingDoc(updatedDoc)) {
                    dispatch(DeployQueueActions(crudConfig).update(updatedDoc[uiConfig.primaryKey], updatedDoc))
                        .then((resp) => {
                            resolve(resp);
                        })
                        .catch((e) => {
                            reject(e);
                        });
                } else {
                    reject('Matching document already exists.');
                }
            }
        });
    };

    const controlButtons = [
        <Button
            key={'refresh-queue'}
            appearance="tertiary"
            icon="refresh"
            loading={refreshing}
            onClick={refreshQueue}
        >
            Refresh
        </Button>
    ];

    // This condition is broken, to fix check for 'crudConfig.functions.insert' instead
    if (crudConfig.insert) {
        controlButtons.push(
            <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
                Add New Deployment
            </Button>
        );
    }
    if (process.env.FEATURE_PURGE_DEPLOYMENT_QUEUE !== 'false' && crudConfig.functions.purge && tableData.length > 0) {
        controlButtons.push(
            <Button key={'purge-queue'} appearance="tertiary" onClick={() => setPurging(true)} icon="delete">
                Purge
            </Button>
        );
    }

    return (
        <div>
            <Table
                withHeader={true}
                columns={columns}
                data={tableData}
                withActions={actions !== null}
                id={'deploy-queue-manager'}
                label={'Deploy Queue'}
                showControlDeck={true}
                paginated={true}
                initialState={{ ...initialState, pageSize: 100 }}
                headerButtons={controlButtons}
            />
            {editing != null && (
                <AddEdit
                    formModel={translateUIConfigForAddEdit(uiConfig)}
                    isNew={editing === 'new'}
                    saveDisabled={saveDisabled}
                    data={maybeFetchEditData()}
                    closeDialog={closeAddEdit}
                    onApply={applyChanges}
                />
            )}
            {deleting != null && <Delete data={fetchDeleteData()} closeDialog={closeDelete} onDelete={onDelete} />}
            {purging != null && <Purge closeDialog={() => setPurging(null)} onPurge={onPurge} />}
        </div>
    );
};

export default DeployQueueManager;
